import React, { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

type DeleteConfirmDialogProps = {
  /** ダイアログの表示/非表示 */
  open: boolean;
  /** ダイアログを閉じるハンドラ */
  onClose: () => void;
  /** 削除実行ハンドラ */
  onConfirm: () => void;
  /** ダイアログタイトル（省略時: "削除確認"） */
  title?: string;
  /** alertモードで入力を求める文言 */
  alertConfirmText?: string;
  /** ダイアログ内に表示するカスタムコンテンツ */
  children?: React.ReactNode;
  /** 実行ボタンのラベル（省略時: "実行"） */
  confirmLabel?: string;
  /** 外部から実行ボタンの非活性を追加制御する（必須フォームのバリデーション用） */
  confirmDisabled?: boolean;
};

/**
 * 共通削除確認ダイアログ
 *
 * - 通常モード: children のみ表示し、すぐに実行可能
 * - alertモード: alertConfirmText を指定すると、その文言を入力しないと実行不可
 * - confirmDisabled で外部から実行ボタンの非活性を追加制御可能
 */
const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title = '削除確認',
  alertConfirmText,
  children,
  confirmLabel = '実行',
  confirmDisabled = false,
}) => {
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (open) {
      setInputValue('');
    }
  }, [open]);

  const isAlertMode = !!alertConfirmText;
  const alertMatched = isAlertMode ? inputValue === alertConfirmText : true;
  const isConfirmEnabled = alertMatched && !confirmDisabled;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        elevation: 0,
        sx: { borderRadius: 3, border: 1, borderColor: 'divider' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <WarningAmberIcon color="error" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
        </Stack>
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {children}

          {isAlertMode && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                削除するには「{alertConfirmText}」を入力してください。
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={alertConfirmText}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={!isConfirmEnabled}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmDialog;
